import {
  ComparisonOperations,
  Operations,
} from '../../../../common/constants/constants';
import { MetricFormatTypeId } from '../../../../common/graphql/generated/graphql-sdk';
import { EmployeeId } from '../../../../common/v2/api/employee/types';
import {
  TENURE_MONTH_IN_DAYS,
  TENURE_YEAR_IN_DAYS,
} from '../../../../common/v2/utils-display';

/** Comparison filter applied only to the Data Explorer Value (metric) column. */
export interface PbsDrillDownValueFilter {
  operation: ComparisonOperations;
  /**
   * Threshold in the same units as `metricValueByEmployeeId` (days for tenure,
   * percentage points for %, raw amount for salary/count, etc.). Null = inactive.
   */
  threshold: number | null;
  /**
   * When set, EQUAL matches the tenure display bucket (years + months) rather than
   * an exact day count — display floors days, so exact-day equality almost never hits.
   */
  tenureEqual?: { years: number; months: number } | null;
}

export const VALUE_FILTER_OPERATIONS: ComparisonOperations[] = [
  Operations.GREATER_THAN,
  Operations.GREATER_THAN_OR_EQUAL_TO,
  Operations.LESS_THAN,
  Operations.LESS_THAN_OR_EQUAL_TO,
  Operations.EQUAL,
];

export const DEFAULT_VALUE_FILTER_OPERATION: ComparisonOperations =
  Operations.GREATER_THAN;

/** Short tokens for CSV filenames (filesystem-safe). */
const FILENAME_OP_TOKEN: Record<ComparisonOperations, string> = {
  [Operations.GREATER_THAN]: 'gt',
  [Operations.GREATER_THAN_OR_EQUAL_TO]: 'gte',
  [Operations.LESS_THAN]: 'lt',
  [Operations.LESS_THAN_OR_EQUAL_TO]: 'lte',
  [Operations.EQUAL]: 'eq',
};

export type ValueFilterInputKind =
  | 'tenureYearsMonths'
  | 'percentage'
  | 'multiple'
  | 'number';

export function getValueFilterInputKind(
  formatType: MetricFormatTypeId | undefined
): ValueFilterInputKind {
  switch (formatType) {
    case MetricFormatTypeId.Pmft004DaysToYearMonth:
      return 'tenureYearsMonths';
    case MetricFormatTypeId.Pmft002Percentage:
    case MetricFormatTypeId.Pmft014FloatingPercentage1:
    case MetricFormatTypeId.Pmft015FloatingPercentage2:
      return 'percentage';
    case MetricFormatTypeId.Pmft008Multiple:
      return 'multiple';
    default:
      return 'number';
  }
}

export function yearsMonthsFromDays(days: number): {
  years: number;
  months: number;
} {
  const sign = days < 0 ? -1 : 1;
  const absoluteDays = Math.abs(days);
  const years = Math.floor(absoluteDays / TENURE_YEAR_IN_DAYS);
  const months = Math.floor(
    (absoluteDays % TENURE_YEAR_IN_DAYS) / TENURE_MONTH_IN_DAYS
  );
  return { years: sign * years, months };
}

/**
 * Inverse of the tenure display conversion (same constants as formatting).
 * Months are not clamped — 1y 14m is valid input and equals more days than 2y 0m.
 */
export function daysFromYearsMonths(years: number, months: number): number {
  const sign = years < 0 || months < 0 ? -1 : 1;
  return (
    sign *
    (Math.abs(years) * TENURE_YEAR_IN_DAYS +
      Math.abs(months) * TENURE_MONTH_IN_DAYS)
  );
}

export function parseValueFilterThreshold(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

export function parseNonNegativeInt(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return null;
  return n;
}

/**
 * Whether a metric value satisfies the comparison. Missing values never match an
 * active filter (they cannot be known to satisfy N ? threshold).
 */
export function matchesValueFilter(
  metricValue: number | undefined,
  filter: PbsDrillDownValueFilter
): boolean {
  if (filter.threshold == null && filter.tenureEqual == null) return true;
  if (metricValue == null || !Number.isFinite(metricValue)) return false;

  if (filter.operation === Operations.EQUAL && filter.tenureEqual != null) {
    const { years, months } = yearsMonthsFromDays(metricValue);
    return (
      years === filter.tenureEqual.years && months === filter.tenureEqual.months
    );
  }

  if (filter.threshold == null) return true;
  const threshold = filter.threshold;
  switch (filter.operation) {
    case Operations.GREATER_THAN:
      return metricValue > threshold;
    case Operations.GREATER_THAN_OR_EQUAL_TO:
      return metricValue >= threshold;
    case Operations.LESS_THAN:
      return metricValue < threshold;
    case Operations.LESS_THAN_OR_EQUAL_TO:
      return metricValue <= threshold;
    case Operations.EQUAL:
      return metricValue === threshold;
    default:
      return true;
  }
}

export function filterEmployeeIdsByValue(
  employeeIds: EmployeeId[],
  metricValueByEmployeeId: Record<EmployeeId, number>,
  filter: PbsDrillDownValueFilter
): EmployeeId[] {
  if (filter.threshold == null && filter.tenureEqual == null) {
    return employeeIds;
  }
  return employeeIds.filter((id) =>
    matchesValueFilter(metricValueByEmployeeId[id], filter)
  );
}

export function isValueFilterActive(
  filter: PbsDrillDownValueFilter | null | undefined
): boolean {
  if (filter == null) return false;
  return filter.threshold != null || filter.tenureEqual != null;
}

/** Make a formatted value (e.g. `40y 0m`, `$1,000`, `50%`) filesystem-safe. */
function sanitizeThresholdToken(formatted: string): string {
  const token = formatted
    .trim()
    .replace(/\s+/g, '')
    .replace(/,/g, '')
    .replace(/%/g, 'pct')
    .replace(/[^0-9A-Za-z._+-]/g, '');
  return token;
}

/**
 * Filename fragment when a Value filter is active, e.g. `_value_gt_40y0m` or
 * `_value_eq_50pct`. When `formatThreshold` is provided the token mirrors the
 * value as shown in the UI (formatted, then sanitized); otherwise it falls back
 * to the raw threshold. Inactive filter yields ''.
 */
export function formatValueFilterFilenameSuffix(
  filter: PbsDrillDownValueFilter | null | undefined,
  formatThreshold?: (raw: number) => string
): string {
  if (!isValueFilterActive(filter) || filter == null) return '';
  const op = FILENAME_OP_TOKEN[filter.operation] ?? 'eq';

  const raw =
    filter.tenureEqual != null
      ? daysFromYearsMonths(filter.tenureEqual.years, filter.tenureEqual.months)
      : filter.threshold;
  if (raw == null) return '';

  if (formatThreshold) {
    const token = sanitizeThresholdToken(formatThreshold(raw));
    if (token !== '') return `_value_${op}_${token}`;
  }

  if (filter.tenureEqual != null) {
    const { years, months } = filter.tenureEqual;
    return `_value_${op}_${years}y${months}m`;
  }
  const thresholdToken = String(filter.threshold).replace(/[^0-9eE+\-.]/g, '');
  return `_value_${op}_${thresholdToken}`;
}
